import { CopyOutlined } from '@ant-design/icons';
import { Alert, DatePicker, message, Radio, Spin, Typography } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { UserContext } from '../../../../UserContext';
import { HWButton } from '../../../../components/shared/StyledButton';

dayjs.extend(utc);

const { Title } = Typography;

interface CrmVerifications {
  need_auth?: boolean;
  check_web_login?: boolean;
  client_url?: string;
  [key: string]: boolean | string | undefined;
}

const VerifyNewClientPage: React.FC<{ apiUrl: string }> = ({ apiUrl }) => {
  const { clientId } = useParams<{ clientId: string }>();
  const user = useContext(UserContext);
  const [loading, setLoading] = useState(true);
  const [client, setClient] = useState<Record<string, unknown> | null>(null);
  const [backfillReady, setBackfillReady] = useState(false);
  const [crmVerifications, setCrmVerifications] = useState<CrmVerifications>(
    {},
  );
  const [error, setError] = useState<string | null>(null);
  const [loadingBackfill, setLoadingBackfill] = useState(false);
  const [checkingWebLogin, setCheckingWebLogin] = useState(false);
  const [executionMode, setExecutionMode] = useState<'batch' | 'container'>(
    'batch',
  );
  const [scheduledFor, setScheduledFor] = useState<Dayjs | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (user == null) {
      return;
    }
    setLoading(true);
    void fetch(`${apiUrl}/api/clients/${clientId}/verify-new`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${user.accessToken}`,
      },
    })
      .then(res => res.json())
      .then(data => {
        if (data.status === 'success') {
          setClient(data.client_config);
          setBackfillReady(data.backfill_ready);
          setCrmVerifications(data.crm_verifications || {});
        } else {
          setError(data.error || 'Unknown error');
        }
      })
      .catch(() => setError('Failed to load client verification info.'))
      .finally(() => setLoading(false));
  }, [apiUrl, user?.accessToken]);

  const handleStartBackfill = async () => {
    if (user == null) {
      return;
    }
    try {
      setLoadingBackfill(true);

      // Build query parameters
      const params = new URLSearchParams({
        execution_mode: executionMode,
      });

      // Add start_at only if a date is selected
      if (scheduledFor) {
        params.append('start_at', scheduledFor.utc().format());
      }

      const response = await fetch(
        `${apiUrl}/api/clients/${clientId}/backfill?${params.toString()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user.accessToken}`,
          },
        },
      );
      if (!response.ok) {
        throw new Error(`${response.status} - ${await response.text()}`);
      }

      const data = await response.json();

      // Show different message based on whether it was scheduled or started immediately
      if (data.status === 'scheduled' && data.scheduled_for) {
        const scheduledTime = dayjs
          .utc(data.scheduled_for)
          .local()
          .format('MMMM D, YYYY h:mm A');
        await message.success(`Backfill scheduled for ${scheduledTime}`);
      } else {
        await message.success('Backfill started successfully');
      }

      navigate(`/client/${clientId}/backfill-status`);
    } catch (error) {
      setError('Failed to start backfill.');
    } finally {
      setLoadingBackfill(false);
    }
  };

  const handleCheckWebLogin = async () => {
    if (user == null) {
      return;
    }
    setCheckingWebLogin(true);
    setError(null);
    try {
      const response = await fetch(
        `${apiUrl}/api/clients/${clientId}/fub-web-login-check`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user.accessToken}`,
          },
        },
      );
      const data = await response.json();
      if (data.status === 'success') {
        await message.success(
          'Web login validated! You can now proceed with backfill.',
        );
        // Refresh verification state to update UI and enable backfill
        setLoading(true);
        void fetch(`${apiUrl}/api/clients/${clientId}/verify-new`, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user.accessToken}`,
          },
        })
          .then(res => res.json())
          .then(data => {
            if (data.status === 'success') {
              setClient(data.client_config);
              setBackfillReady(data.backfill_ready);
              setCrmVerifications(data.crm_verifications || {});
            } else {
              setError(data.error || 'Unknown error');
            }
          })
          .catch(() => setError('Failed to load client verification info.'))
          .finally(() => setLoading(false));
      } else {
        await message.error('Web login check failed. Please try again.');
      }
    } catch (err) {
      await message.error('Web login check failed. Please try again.');
    } finally {
      setCheckingWebLogin(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <p>Loading verification status...</p>
      </div>
    );
  }

  return (
    <div>
      <Title level={1}>
        Onboarding - {(client?.friendly_name as string) || 'Client'}
      </Title>
      <div
        style={{
          backgroundColor: '#fff',
          padding: 20,
          borderRadius: 8,
          maxWidth: 800,
          margin: '0 auto',
        }}
      >
        <Title level={2}>Account Verification Status</Title>
        {backfillReady && (
          <Alert
            message="Backfilling is ready to proceed."
            showIcon
            style={{ marginBottom: 20 }}
            type="success"
          />
        )}
        {error && (
          <Alert
            message={error}
            showIcon
            style={{ marginBottom: '20px' }}
            type="warning"
          />
        )}
        <div style={{ margin: '20px 0' }}>
          {!backfillReady && client?.crm_type === 'fub' && (
            <div>
              <Alert
                message={
                  'To finish onboarding, you must validate the Follow Up Boss web login. Click the button below to check. If successful, you can proceed with backfill. If it fails, please try again.'
                }
                showIcon
                style={{ marginBottom: 12 }}
                type="info"
              />
              {crmVerifications.check_web_login && (
                <HWButton
                  loading={checkingWebLogin}
                  onClick={() => void handleCheckWebLogin()}
                  style={{ marginTop: 10 }}
                  type="primary"
                >
                  Check Web Login
                </HWButton>
              )}
              {!crmVerifications.check_web_login && (
                <div style={{ marginTop: 10 }}>
                  No web login check required at this time.
                </div>
              )}
            </div>
          )}
          {!backfillReady &&
            (client?.crm_type === 'lofty' ||
              client?.crm_type === 'moxiworks') && (
              <div>
                <Alert
                  message={
                    <span>
                      <b>Step 1:</b> Copy the link below and send it to your
                      contact at{' '}
                      {(client?.friendly_name as string) || 'the client'}.
                      <br />
                      <b>Step 2:</b> Ask the client to click the link and
                      authorize HouseWhisper access to their{' '}
                      {client?.crm_type as string} account. <br />
                      <b>Step 3:</b> Once the client has authorized, click
                      &quot;Refresh Page&quot; below to check if you can
                      backfill.
                      <br />
                      <div
                        style={{
                          margin: '16px 0',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <code
                          style={{
                            fontSize: 16,
                            padding: 4,
                            background: '#f5f5f5',
                            borderRadius: 4,
                          }}
                        >
                          {window.location.origin +
                            `/${client?.crm_type as string}/${clientId}`}
                        </code>
                        <CopyOutlined
                          onClick={() => {
                            void navigator.clipboard.writeText(
                              window.location.origin +
                                `/${client?.crm_type as string}/${clientId}`,
                            );
                            void message.success('Link copied to clipboard');
                          }}
                          style={{
                            marginLeft: 8,
                            fontSize: 18,
                            cursor: 'pointer',
                          }}
                        />
                      </div>
                    </span>
                  }
                  showIcon
                  style={{ margin: '20px 0' }}
                  type="info"
                />
                <HWButton
                  onClick={() => void window.location.reload()}
                  type="default"
                >
                  Refresh Page
                </HWButton>
              </div>
            )}
          {!backfillReady &&
            client &&
            client.crm_type !== 'fub' &&
            client.crm_type !== 'lofty' &&
            client.crm_type !== 'moxiworks' && (
              <Alert
                message="Nothing else to validate for this CRM."
                showIcon
                style={{ margin: '20px 0' }}
                type="info"
              />
            )}
        </div>
        <div style={{ marginTop: '30px' }}>
          {backfillReady && (
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
            >
              <div>
                <div
                  style={{
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: 500,
                  }}
                >
                  Execution Mode
                </div>
                <Radio.Group
                  onChange={e => setExecutionMode(e.target.value)}
                  value={executionMode}
                >
                  <Radio value="batch">Batch</Radio>
                  <Radio value="container">Container</Radio>
                </Radio.Group>
              </div>
              <div>
                <div
                  style={{
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: 500,
                  }}
                >
                  Schedule For (Optional) - PST
                </div>
                <DatePicker
                  disabledDate={current =>
                    current && current < dayjs().startOf('minute')
                  }
                  format="MMMM D, YYYY h:mm A"
                  onChange={value => setScheduledFor(value)}
                  placeholder="Select date and time (PST)"
                  showTime={{ format: 'h:mm A', use12Hours: true }}
                  style={{ width: 300 }}
                  value={scheduledFor}
                />
                <div
                  style={{
                    marginTop: '4px',
                    fontSize: '12px',
                    color: 'rgba(0, 0, 0, 0.45)',
                  }}
                >
                  Leave empty to start immediately. For Sierra clients, schedule
                  after 11pm PST.
                </div>
              </div>
            </div>
          )}
          <div style={{ marginTop: '16px' }}>
            <HWButton
              disabled={!backfillReady}
              loading={loadingBackfill}
              onClick={() => void handleStartBackfill()}
              size="large"
              type="primary"
            >
              {scheduledFor ? 'Schedule Backfill' : 'Start Backfill'}
            </HWButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyNewClientPage;
